import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/constants";

export default function Home() {
  useEffect(() => {
    document.title = "OVI — The Sound of Making";
  }, []);

  return (
    <div>
      {/* HERO */}
      <section
        className="relative overflow-hidden ovi-texture bg-[var(--ovi-cream)]"
        data-testid="home-hero"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 lg:items-stretch min-h-[60vh] lg:min-h-[78vh]">
          <div className="flex items-center px-4 sm:px-6 lg:pl-[max(2rem,calc((100vw-1280px)/2+2rem))] lg:pr-10 xl:pr-16 pt-14 sm:pt-20 lg:pt-0 pb-10 lg:pb-0">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
              className="w-full max-w-2xl"
            >
              <p className="text-[11px] sm:text-xs uppercase tracking-[0.28em] text-[var(--ovi-terracotta)] font-semibold mb-6">
                · Made in India ·
              </p>
              <h1
                className="font-serif-display text-[clamp(64px,12vw,168px)] leading-[0.88] tracking-[-0.03em] text-[var(--ovi-indigo)]"
                data-testid="home-hero-wordmark"
              >
                OVI
              </h1>
              <p
                className="mt-6 sm:mt-8 font-serif-display italic text-2xl sm:text-3xl lg:text-4xl text-[var(--ovi-ink)]"
                data-testid="home-hero-tagline"
              >
                The Sound of Making.
              </p>
              <p
                className="mt-4 max-w-xl text-base sm:text-lg text-[var(--ovi-muted-ink)] leading-relaxed"
                data-testid="home-hero-subline"
              >
                {BRAND.subline}
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-3 sm:gap-4">
                <Button
                  asChild
                  className="bg-[var(--ovi-terracotta)] hover:bg-[var(--ovi-terracotta-dark)] text-[var(--ovi-cream-2)] rounded-full h-12 px-6 active:scale-[0.98] transition-[background-color,transform] duration-200"
                >
                  <Link to="/makers" data-testid="home-hero-primary-cta">
                    Meet our makers
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-[var(--ovi-indigo)] text-[var(--ovi-indigo)] hover:bg-[rgba(30,42,71,0.06)] hover:text-[var(--ovi-indigo)] rounded-full h-12 px-6"
                >
                  <Link to="/verified" data-testid="home-hero-secondary-cta">
                    Partner with OVI
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, ease: "easeOut", delay: 0.15 }}
            className="relative min-h-[420px] sm:min-h-[520px] lg:min-h-full lg:h-auto"
            data-testid="home-hero-image"
          >
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/static/products/hero-ovi-weaver.png`}
              alt="A weaver at her traditional loom, weaving colourful cloth in warm golden light"
              className="absolute inset-0 h-full w-full object-cover"
              loading="eager"
            />
            <div className="hidden lg:block absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[var(--ovi-cream)] to-transparent pointer-events-none" />
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[rgba(20,22,27,0.35)] to-transparent pointer-events-none" />
            <div className="absolute bottom-4 left-4 right-4 lg:bottom-6 lg:left-6 text-[10px] sm:text-xs uppercase tracking-[0.22em] text-[var(--ovi-cream-2)] font-semibold drop-shadow">
              · Solapur, Maharashtra ·
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST SECTION */}
      <section
        className="bg-[var(--ovi-cream-2)] border-y border-[var(--ovi-border)]"
        data-testid="home-trust-section"
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
            <TrustCard
              eyebrow="01"
              title="Direct from factory"
              body="No middlemen. You pay the maker, not the markup."
              testid="home-trust-item-direct"
            />
            <TrustCard
              eyebrow="02"
              title="India's finest craft"
              body="Generations of weavers. Pit-loom heritage, modern finish."
              testid="home-trust-item-craft"
            />
            <TrustCard
              eyebrow="03"
              title="Shipped across India"
              body="Fast, tracked delivery to every pincode. Order on WhatsApp."
              testid="home-trust-item-shipping"
            />
          </div>
        </div>
      </section>

      {/* THREE WAYS OVI WORKS — indigo dark section */}
      <section
        className="relative bg-[var(--ovi-indigo)] text-[var(--ovi-cream-2)] ovi-noise"
        data-testid="home-three-ways"
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.28em] text-[#E4B79A] font-semibold mb-5">
              The platform
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-[var(--ovi-cream-2)]">
              Three Ways OVI Works
            </h2>
            <p className="mt-5 text-base sm:text-lg text-[var(--ovi-cream-2)]/80 leading-relaxed">
              OVI is the complete digital infrastructure of Solapur's textile economy —
              operating across three lanes, all at once.
            </p>
          </div>

          <div className="mt-12 sm:mt-14 grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
            <LaneCard
              number="01"
              title="OVI Direct"
              audience="For buyers across India"
              body="Browse India's finest textiles and order directly from the maker via WhatsApp. No middlemen. No markup."
              badge="Launching soon"
              testid="home-lane-direct"
            />
            <LaneCard
              number="02"
              title="OVI Trade"
              audience="For retailers, hotels, designers and businesses"
              body="Source Solapur textiles in bulk directly from verified manufacturers with GST invoices and quality guarantee."
              cta={{ to: "/trade", label: "Bulk Inquiry" }}
              testid="home-lane-trade"
            />
            <LaneCard
              number="03"
              title="OVI Verified"
              audience="For government bodies, NGOs and institutions"
              body="Access India's most comprehensive verified digital registry of Solapur's textile makers and manufacturers."
              cta={{ to: "/verified", label: "Partner with OVI" }}
              testid="home-lane-verified"
            />
          </div>
        </div>
      </section>

      {/* APPLY TO BECOME A MAKER — replaces featured products at soft launch */}
      <section className="bg-[var(--ovi-cream)]" data-testid="home-apply-maker-section">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-28 text-center">
          <p className="text-xs uppercase tracking-[0.28em] text-[var(--ovi-terracotta)] font-semibold mb-6">
            Apply to become a maker
          </p>
          <h2
            className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[var(--ovi-indigo)] leading-[1.1] tracking-tight"
            data-testid="home-apply-maker-title"
          >
            We are onboarding India's first verified makers.
          </h2>
          <p
            className="mt-5 text-base sm:text-lg text-[var(--ovi-muted-ink)] leading-relaxed max-w-2xl mx-auto"
            data-testid="home-apply-maker-subline"
          >
            Join the founding cohort.
          </p>
          <div className="mt-10">
            <Button
              asChild
              className="bg-[var(--ovi-terracotta)] hover:bg-[var(--ovi-terracotta-dark)] text-[var(--ovi-cream-2)] rounded-full h-12 px-7 active:scale-[0.98] transition-[background-color,transform] duration-200"
            >
              <Link to="/makers#maker-application" data-testid="home-apply-maker-cta">
                Apply Now
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* SOLAPUR ECONOMY BY NUMBERS — terracotta section */}
      <section
        className="relative bg-[var(--ovi-terracotta)] text-[var(--ovi-cream-2)] overflow-hidden ovi-noise"
        data-testid="home-economy-numbers"
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.28em] text-[var(--ovi-cream-2)]/85 font-semibold mb-5">
              The economy
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1]">
              The Solapur Textile Economy<br className="hidden sm:block" /> By Numbers
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10 sm:gap-y-12">
            <Stat
              value="50,000+"
              label="Powerloom units"
              testid="home-stat-powerlooms"
            />
            <Stat
              value="2 Lakh+"
              label="Workers in textile cluster"
              testid="home-stat-workers"
            />
            <Stat
              value="2 GI Tags"
              label="Solapur Chaddar & Terry Towel"
              testid="home-stat-gi"
            />
            <Stat
              value="Zero"
              label="Direct digital platform — until OVI"
              testid="home-stat-zero"
            />
          </div>
        </div>
      </section>

      {/* BRAND STORY */}
      <section
        className="relative bg-[var(--ovi-indigo)] text-[var(--ovi-cream-2)] overflow-hidden ovi-noise"
        data-testid="home-brand-story"
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.28em] text-[#E4B79A] font-semibold mb-5">
              The story of OVI
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-[var(--ovi-cream-2)]">
              Born in Solapur, built from within — for India.
            </h2>
            <div className="mt-6 space-y-5 text-base sm:text-lg leading-relaxed text-[var(--ovi-cream-2)]/85 max-w-2xl">
              <p>
                OVI was born in Solapur — not discovered from the outside, but built
                from within. Named after the folk songs Marathi women sing while
                weaving, OVI exists because the makers behind India's finest
                textiles had no direct voice to the people buying their work.
              </p>
              <p>
                No brand. No platform. No name. Just craft, passed through hands
                for generations, sold through layers of middlemen who took the
                margin and left the story behind.
              </p>
              <p className="text-[var(--ovi-cream-2)] font-medium">
                We are changing that — one maker at a time, one cluster at a time, across India.
              </p>
            </div>
            <div className="mt-8">
              <Button
                asChild
                className="bg-[var(--ovi-terracotta)] hover:bg-[var(--ovi-terracotta-dark)] text-[var(--ovi-cream-2)] rounded-full h-11 px-6 active:scale-[0.98] transition-[background-color,transform] duration-200"
              >
                <Link to="/about" data-testid="home-brand-story-cta">
                  Read our story
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-[rgba(245,239,230,0.18)]">
              <img
                src="https://images.unsplash.com/photo-1622825312256-fec995a8bbdd?auto=format&fit=crop&w=1200&q=80"
                alt="Hands weaving at a traditional loom in Solapur"
                className="w-full h-[360px] sm:h-[440px] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[rgba(30,42,71,0.5)] via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 text-xs uppercase tracking-[0.22em] text-[var(--ovi-cream-2)] font-semibold">
                Solapur, Maharashtra
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const TrustCard = ({ eyebrow, title, body, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
    className="relative bg-[var(--ovi-cream)] border border-[var(--ovi-border)] rounded-2xl p-6 sm:p-7"
    data-testid={testid}
  >
    <span className="absolute -top-3 left-6 text-[11px] uppercase tracking-[0.24em] font-semibold text-[var(--ovi-terracotta)] bg-[var(--ovi-cream)] px-2">
      {eyebrow}
    </span>
    <h3 className="font-serif-display text-xl sm:text-2xl text-[var(--ovi-indigo)]">{title}</h3>
    <p className="mt-3 text-sm sm:text-base text-[var(--ovi-muted-ink)] leading-relaxed">
      {body}
    </p>
  </motion.div>
);

const LaneCard = ({ number, title, audience, body, cta, badge, testid }) => (
  <motion.article
    initial={{ opacity: 0, y: 14 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55, ease: [0.2, 0.7, 0.2, 1] }}
    className="flex flex-col p-6 sm:p-7 rounded-2xl bg-[rgba(245,239,230,0.06)] border border-[rgba(245,239,230,0.16)] hover:border-[rgba(200,85,61,0.55)] transition-colors duration-300"
    data-testid={testid}
  >
    <span className="text-[11px] uppercase tracking-[0.28em] font-semibold text-[var(--ovi-terracotta)]">
      {number}
    </span>
    <h3 className="mt-3 font-serif-display text-2xl sm:text-3xl text-[var(--ovi-cream-2)] leading-tight">
      {title}
    </h3>
    <p className="mt-2 text-xs sm:text-sm uppercase tracking-[0.14em] text-[#E4B79A] font-medium">
      {audience}
    </p>
    <p className="mt-5 text-sm sm:text-base text-[var(--ovi-cream-2)]/80 leading-relaxed flex-1">
      {body}
    </p>
    {cta ? (
      <Link
        to={cta.to}
        className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[var(--ovi-cream-2)] hover:text-[var(--ovi-terracotta)] transition-colors"
      >
        {cta.label}
        <ArrowRight className="h-4 w-4" />
      </Link>
    ) : badge ? (
      <span
        className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-[var(--ovi-terracotta)] bg-[rgba(200,85,61,0.14)] border border-[rgba(200,85,61,0.35)] rounded-full px-3 py-1.5 w-fit"
        data-testid={`${testid}-badge`}
      >
        · {badge} ·
      </span>
    ) : null}
  </motion.article>
);

const Stat = ({ value, label, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
    className=""
    data-testid={testid}
  >
    <p className="font-serif-display text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-none text-[var(--ovi-cream-2)] tracking-tight whitespace-nowrap">
      {value}
    </p>
    <p className="mt-3 text-xs sm:text-sm uppercase tracking-[0.18em] text-[var(--ovi-cream-2)]/85 font-semibold">
      {label}
    </p>
  </motion.div>
);
